<script setup lang="ts">
export interface PagesData extends payloadData {
  docs: Page[]
}

const globalPageGroups = useState<any[]>('globalPageGroups')

const route = useRoute()
const { locale } = useI18n()
const { slug } = route.params

const runtimeConfig = useRuntimeConfig()

const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

// A page that sits in a page group as a level or child only has a route under
// its group's main page, so a bare /<slug> request is sent to that path.
const groupedRoute = (() => {
  for (const group of globalPageGroups.value ?? []) {
    for (const level of group.levels ?? []) {
      if (level.main?.slug === slug) {
        return { name: 'slug-path', params: { slug: group.main?.slug, path: [level.main.slug] } }
      }
      const child = level.children?.find((c: any) => c.slug === slug)
      if (child) {
        return { name: 'slug-path', params: { slug: group.main?.slug, path: [level.main?.slug, child.slug] } }
      }
    }
  }
  return null
})()

if (groupedRoute) {
  await navigateTo(useLocaleRoute()(groupedRoute), { redirectCode: 301 })
}

const { data, error } = await useAsyncData(
  () => `page-${slug}-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: SinglePageQuery,
      variables: {
        slug: { equals: slug },
        locale: locale.value,
        isPreview: isPreview.value,
        // with `draft: true` payload returns each doc's latest version, whatever its
        // status -- so preview must accept both, or a doc with no pending draft
        // (latest version published) matches nothing and the page renders empty
        statuses: isPreview.value ? ['draft', 'published'] : ['published'],
      },
      isPreview: isPreview.value,
    },
  }),
  { transform: (data: PagesData) => transformPayloadData<PagesData>(data) as Page }
)

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching page' })
}

if (!data.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

usePageSeo({
  title: () => data.value?.title,
})

const pageNav = computed(() => usePageNavigation(data.value?.content))
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="data?.title"
        :route="{
          name: 'slug',
          title: data?.title,
          slug: slug,
        }"
        :include-breadcrumbs="true"
        :page-nav="pageNav"
      />
    </template>
    
    <template #page-nav>
      <page-nav
        v-if="pageNav"
      />
    </template>
    
    <template #default>
      <pages-slug
        v-if="data"
        :data="data"
      />
    </template>
  </page-layout>
</template>
